/**
 * The meld family: Rummy, Gin, Canasta, Conquian, Five Hundred Rummy.
 *
 * Collect cards into **sets** (three or more of a rank) and **runs** (three or
 * more in sequence in one suit), lay them down, and go out.
 *
 * What the family shares is the shape of a meld and the arithmetic on it:
 * whether a group of cards is one, whether a card can be added to one, and what
 * it is worth. Everything above that — drawing, discarding, when you may lay
 * down, what going out is worth — varies between members and belongs to the
 * game.
 *
 * Written **before** the first game that uses it, deliberately. A per-game
 * `isSet` is four lines and looks harmless; the reason not to write one is that
 * the *edge* cases are where the family earns its keep, and each game would get
 * them subtly differently. An ace can be low, high, both or neither depending on
 * the house rule; whether it may wrap from king round to two is a real
 * disagreement between real tables; and a joker standing in for a card has to
 * answer "which card is it standing for?" before anything else can be decided.
 * Those are worth getting right once.
 *
 * Pure functions over `CardId[]`, no engine, exactly as `trick-taking.ts` and
 * `shedding.ts` are.
 */
import { ACE_HIGH, ACE_LOW, RANKS, isJoker, rankOf, suitOf } from '../cards.js';
const DEFAULT_MIN = 3;
function minLength(rules) {
    return rules.minLength ?? DEFAULT_MIN;
}
/** Is this card wild, by rank or by being a joker? */
export function isWild(card, rules) {
    if (!card)
        return false;
    if (isJoker(card))
        return rules.jokersWild !== false;
    const rank = rankOf(card);
    if (rank === null)
        return false;
    return (rules.wildRanks ?? []).indexOf(rank) >= 0;
}
/**
 * Split a group into the cards that are what they are, and the ones standing in.
 *
 * Every question below starts here, because a wild card has no rank and no suit
 * of its own and so cannot be compared with anything until the real cards have
 * said what the meld is about.
 */
function split(cards, rules) {
    const real = [];
    let wild = 0;
    for (const card of cards) {
        if (isWild(card, rules))
            wild += 1;
        else
            real.push(card);
    }
    return { real, wild };
}
/**
 * Are there too many wild cards, or nothing real at all?
 *
 * Three jokers are not three of anything. A meld is a claim about actual cards,
 * so at least one has to be present to make the claim about.
 */
function wildsAcceptable(real, wild, rules) {
    if (real === 0)
        return false;
    const most = rules.maxWild;
    if (most !== undefined && wild > most)
        return false;
    return true;
}
/**
 * Three or more of a rank.
 *
 * Suits are irrelevant — which surprises people who expect four-of-a-rank to
 * mean one of each suit. Two cards of the same face from a double pack are
 * allowed here, because the games that use two packs allow them and the ones
 * that do not are playing with one pack and so cannot produce the situation.
 */
export function isSet(cards, rules = {}) {
    if (cards.length < minLength(rules))
        return false;
    const { real, wild } = split(cards, rules);
    if (!wildsAcceptable(real.length, wild, rules))
        return false;
    const first = rankOf(real[0]);
    if (first === null)
        return false;
    for (const card of real) {
        if (rankOf(card) !== first)
            return false;
    }
    return true;
}
/**
 * Where a run's positions live.
 *
 * **Not** a rank order, which is what I reached for first and got wrong. Two
 * orders exist — ace low and ace high — and under `both` a run may use either,
 * so an index taken from one of them is meaningless against the other. Q-K-A
 * read positions out of the ace-high order and compared them against the
 * ace-low one, and Q-K-A stopped being a run.
 *
 * One frame fixes it. Fourteen slots:
 *
 *   0    1    2   ...  12   13
 *   A    2    3   ...  K    A
 *
 * The ace appears twice because it genuinely can be in two places, and the two
 * are thirteen apart rather than adjacent — which is precisely why K-A-2 is not
 * a run and A-2-3 and Q-K-A both are. The shape of the frame carries the rule.
 */
const LOW_ACE = 0;
const HIGH_ACE = RANKS.length;
const FRAME = RANKS.length + 1;
/**
 * Every slot a rank may occupy.
 *
 * Usually one. An ace under `both` has two, and trying each is the only way to
 * answer "is this a run?" without special-casing the ace at every comparison.
 */
function positionsOf(rank, rules) {
    if (rank !== 'A') {
        const at = ACE_LOW.indexOf(rank);
        return at >= 0 ? [at] : [];
    }
    /*
     * A wrapping table has no "ends" for an ace to sit at.
     *
     * The sequence is a circle, so every rank has exactly one place and the
     * ace-position rule stops meaning anything.
     */
    if (rules.wrap === true)
        return [LOW_ACE];
    switch (rules.acePosition ?? 'low') {
        case 'high':
            return [HIGH_ACE];
        case 'both':
            return [LOW_ACE, HIGH_ACE];
        default:
            return [LOW_ACE];
    }
}
/**
 * Is this group a run when it starts at slot `start`?
 *
 * Walks the slots the run would occupy and checks each is either held by the
 * right card or covered by a wild. Wilds are spent as needed, which is what
 * makes "7-joker-9" a run: the joker is standing at the eight.
 */
function runsFrom(cards, suit, start, rules) {
    const circular = rules.wrap === true;
    const size = circular ? RANKS.length : FRAME;
    let spare = 0;
    for (const card of cards)
        if (isWild(card, rules))
            spare += 1;
    const held = new Set();
    for (const card of cards) {
        if (isWild(card, rules))
            continue;
        if (suitOf(card) !== suit)
            return false;
        const rank = rankOf(card);
        if (rank === null)
            return false;
        let placed = false;
        for (const position of positionsOf(rank, rules)) {
            // Only a slot inside the window this run would occupy.
            const offset = circular ? (position - start + size) % size : position - start;
            if (offset < 0 || offset >= cards.length)
                continue;
            // A repeated rank cannot be in a run, and neither can the same ace twice.
            if (held.has(position))
                return false;
            held.add(position);
            placed = true;
            break;
        }
        if (!placed)
            return false;
    }
    for (let step = 0; step < cards.length; step++) {
        const position = circular ? (start + step) % size : start + step;
        if (position >= size)
            return false;
        if (held.has(position))
            continue;
        if (spare === 0)
            return false;
        spare -= 1;
    }
    return true;
}
/**
 * Three or more in sequence in one suit.
 *
 * Every real card must share a suit, and the sequence must not wrap past the
 * end of the order unless the table plays that way. An ace under `both` is
 * tried in each of its positions, which is why this asks "does it run from
 * here?" rather than sorting and comparing neighbours: sorting has to decide
 * where the ace goes before it can sort, and that is the question.
 */
export function isRun(cards, rules = {}) {
    if (cards.length < minLength(rules))
        return false;
    const { real, wild } = split(cards, rules);
    if (!wildsAcceptable(real.length, wild, rules))
        return false;
    const suit = suitOf(real[0]);
    if (suit === null)
        return false;
    const size = rules.wrap === true ? RANKS.length : FRAME;
    for (let start = 0; start < size; start++) {
        if (rules.wrap !== true && start + cards.length > size)
            continue;
        if (runsFrom(cards, suit, start, rules))
            return true;
    }
    return false;
}
/**
 * What this group is, if it is anything.
 *
 * A group is never both. Three of a rank cannot be a sequence in one suit,
 * because a run needs distinct ranks — which is worth a property test rather
 * than a comment, and has one.
 */
export function validMeld(cards, rules = {}) {
    if (isSet(cards, rules))
        return 'set';
    if (isRun(cards, rules))
        return 'run';
    return null;
}
/**
 * May this card be added to a meld already on the table?
 *
 * Answered by asking whether the meld is still a meld with the card in it,
 * rather than by a second set of rules about extending. The two would drift:
 * "you may add a fourth to a set" and "you may add to either end of a run" are
 * restatements of the same fact, and restating a rule is how the two copies
 * come to disagree.
 */
export function canLayOff(card, meld, rules = {}) {
    if (!card)
        return false;
    const was = validMeld(meld, rules);
    if (was === null)
        return false;
    return validMeld([...meld, card], rules) === was;
}
/**
 * The usual Rummy values: face cards ten, ace one, everything else its pips.
 *
 * Offered as a starting point rather than as the truth. Gin scores an ace at
 * one and Canasta at twenty, which is exactly why `meldScore` takes the values
 * rather than knowing them.
 */
export const RUMMY_VALUES = {
    A: 1,
    '2': 2,
    '3': 3,
    '4': 4,
    '5': 5,
    '6': 6,
    '7': 7,
    '8': 8,
    '9': 9,
    T: 10,
    J: 10,
    Q: 10,
    K: 10,
    // Jokers have no rank, so they are keyed by name.
    JOKER: 15,
};
/**
 * What a group of cards is worth.
 *
 * Used both ways round by most of the family: what your melds earn, and what
 * the cards left in your hand cost you. It is the same arithmetic, which is why
 * there is one function.
 *
 * A card the values do not mention is worth nothing rather than throwing. A
 * scoring table is a house rule, and a house rule that omits a card should not
 * be able to crash a hand at the moment of scoring — the one moment where
 * failing loudly is worst.
 */
export function meldScore(cards, values = RUMMY_VALUES) {
    let total = 0;
    for (const card of cards) {
        if (isJoker(card)) {
            total += values['JOKER'] ?? 0;
            continue;
        }
        const rank = rankOf(card);
        if (rank === null)
            continue;
        total += values[rank] ?? 0;
    }
    return total;
}
/**
 * Every meld that can be made from a hand, as card groups.
 *
 * For hinting and for bots — never for legality, which is `validMeld` on what
 * the player actually chose. Deliberately only the *minimum-length* melds:
 * every longer one is a shorter one plus a lay-off, so listing both would offer
 * the same meld many times over and the count grows fast enough to matter on a
 * phone.
 */
export function possibleMelds(hand, rules = {}) {
    const size = minLength(rules);
    const found = [];
    if (hand.length < size)
        return found;
    const combo = [];
    const walk = (from) => {
        if (combo.length === size) {
            const cards = combo.map((index) => hand[index]);
            if (validMeld(cards, rules) !== null)
                found.push(cards);
            return;
        }
        for (let index = from; index < hand.length; index++) {
            combo.push(index);
            walk(index + 1);
            combo.pop();
        }
    };
    walk(0);
    return found;
}
/**
 * The ranks, in the order a run reads, for labelling a choice to a player.
 *
 * For display only. Legality is decided against the fourteen-slot frame above,
 * because under `both` neither of these two orders is the whole truth.
 */
export function runOrder(rules = {}) {
    return rules.acePosition === 'high' ? ACE_HIGH : ACE_LOW;
}
//# sourceMappingURL=melds.js.map